import { Link } from "@mui/material";
import { useOpenUrl } from "@src/mobile/utils/openUrl";
import { type ReactNode } from "react";

interface ChatLinkProps {
  /** The absolute, http(s) href to navigate to. */
  href: string;
  /** Drives the link color. */
  isIncoming: boolean;
  children: ReactNode;
}

/**
 * A chat link that opens via {@link useOpenUrl} so it respects the
 * Capacitor-mobile vs web split (in-app browser on native, new tab on web)
 * instead of calling `window.open` directly.
 */
export function ChatLink(props: ChatLinkProps) {
  const { href, isIncoming, children } = props;
  const openUrl = useOpenUrl();

  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      underline="always"
      // The bubble masks message TEXT from Datadog RUM, but an `href` attribute
      // is recorded verbatim. A user-sent URL can carry PHI in its path/query,
      // so hide the link (and its href) from replay.
      data-dd-privacy="hidden"
      onClick={(event) => {
        // Let the browser handle modified clicks (cmd/ctrl/shift-click,
        // middle-click) natively so "open in new tab/window" still works.
        if (
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          event.button !== 0
        ) {
          return;
        }

        // For a plain click, open through the platform-aware helper rather than
        // the anchor so native uses the in-app browser. The href is kept for
        // hover/right-click/keyboard semantics.
        event.preventDefault();
        void openUrl(href);
      }}
      sx={(theme) => {
        // Incoming (light bubble): the link token, which reads clearly against
        // the light background. Outgoing (dark/brand bubble): the inverted
        // near-white text color, since the link token has too little contrast on
        // the dark bubble.
        const linkColor = isIncoming
          ? theme.palette.link?.base
          : theme.palette.base?.inverted?.strong;

        return {
          fontWeight: theme.typography.fontWeightMedium,
          wordBreak: "break-word",
          // A global app rule (`body a:not(.MuiButtonBase-root)`) paints every
          // anchor with the brand color via BOTH `color` and
          // `-webkit-text-fill-color`. The doubled `&&` selector bumps
          // specificity above it so chat links use the bubble-appropriate color.
          // `WebkitTextFillColor` and `textDecorationColor` must be set
          // explicitly — neither follows `color`.
          "&&": {
            color: linkColor,
            WebkitTextFillColor: linkColor,
            textDecorationColor: linkColor,
          },
        };
      }}
    >
      {children}
    </Link>
  );
}
